import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { trafficLight, fmtEUR, fmtDateShort } from "../lib/format";
import { Building2, User, ArrowUpDown, ArrowUp, ArrowDown, Plus, ExternalLink, Columns3, Check, FileSpreadsheet } from "lucide-react";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const ALL_COLUMNS = [
  { key: "title", label: "Gara / Progetto", sortable: true, align: "left", w: 320, alwaysOn: true },
  { key: "client_name", label: "Cliente", sortable: true, align: "left", w: 220 },
  { key: "type", label: "Tipo", sortable: true, align: "left", w: 100 },
  { key: "deadline", label: "Scadenza", sortable: true, align: "left", w: 110 },
  { key: "_tl", label: "Stato", sortable: true, align: "left", w: 110 },
  { key: "importo_lavori", label: "Importo lavori", sortable: true, align: "right", w: 140 },
  { key: "parcella_calculated", label: "Parcella (tecnico)", sortable: true, align: "right", w: 150 },
  { key: "total_accrued", label: "Maturato", sortable: true, align: "right", w: 110 },
  { key: "total_invoiced", label: "Fatturato", sortable: true, align: "right", w: 110 },
  { key: "total_paid", label: "Pagato", sortable: true, align: "right", w: 110 },
  { key: "cig", label: "CIG", sortable: true, align: "left", w: 120 },
  { key: "cup", label: "CUP", sortable: true, align: "left", w: 120 },
];
const DEFAULT_COLS = ["title", "client_name", "type", "deadline", "_tl", "importo_lavori", "parcella_calculated", "total_accrued", "total_invoiced", "total_paid"];
const STORAGE_KEY = "crm_projects_columns_v1";

function truncate(s, n) {
  if (!s) return "";
  s = String(s);
  return s.length > n ? s.slice(0, n).trimEnd() + "…" : s;
}

export default function ProjectsList() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState({});
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState({ key: "deadline", dir: "asc" });
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (Array.isArray(saved) && saved.length) return saved;
    } catch {}
    return DEFAULT_COLS;
  });

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleCols)); }, [visibleCols]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => toast.error("Errore caricamento"));
    api.get("/clients").then((r) => setClients(Object.fromEntries(r.data.map((c) => [c.id, c]))));
  }, []);

  const enriched = useMemo(() => projects.map((p) => {
    const tl = trafficLight(p.deadline, p.delivered);
    return { ...p, _tl: tl.level, _tl_obj: tl, client_name: clients[p.client_id]?.name || "—" };
  }), [projects, clients]);

  const filtered = useMemo(() => enriched.filter((p) => {
    if (typeFilter && p.type !== typeFilter) return false;
    if (statusFilter && p._tl !== statusFilter) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!p.title.toLowerCase().includes(q) && !p.client_name.toLowerCase().includes(q) && !(p.cig || "").toLowerCase().includes(q)) return false;
    }
    return true;
  }), [enriched, query, typeFilter, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = a[sort.key] ?? (typeof a[sort.key] === "number" ? 0 : "");
      const vb = b[sort.key] ?? (typeof b[sort.key] === "number" ? 0 : "");
      const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sort]);

  const totals = useMemo(() => sorted.reduce((acc, p) => ({
    importo: acc.importo + Number(p.importo_lavori || 0),
    parcella: acc.parcella + Number(p.parcella_calculated || 0),
    accrued: acc.accrued + Number(p.total_accrued || 0),
    invoiced: acc.invoiced + Number(p.total_invoiced || 0),
    paid: acc.paid + Number(p.total_paid || 0),
  }), { importo: 0, parcella: 0, accrued: 0, invoiced: 0, paid: 0 }), [sorted]);

  const toggleSort = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));
  const SortIcon = ({ k }) => sort.key !== k ? <ArrowUpDown className="h-3 w-3 opacity-40" /> : sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;

  const toggleCol = (k) => setVisibleCols((v) => (v.includes(k) ? v.filter((x) => x !== k) : [...v, k]));
  const resetCols = () => setVisibleCols(DEFAULT_COLS);
  const cols = ALL_COLUMNS.filter((c) => visibleCols.includes(c.key));

  const exportXlsx = () => {
    const rawFor = (c, p) => {
      switch (c.key) {
        case "title": return p.title;
        case "client_name": return p.client_name;
        case "type": return p.type === "pa" ? "Pubblica Amm." : "Privato";
        case "deadline":
          if (!p.deadline) return "";
          try { return new Date(p.deadline); } catch { return p.deadline; }
        case "_tl":
          return p._tl === "green" ? "Consegnata" : p._tl === "red" ? "Rosso" : p._tl === "orange" ? "Arancione" : p._tl === "yellow" ? "Giallo" : "-";
        case "importo_lavori": return Number(p.importo_lavori || 0);
        case "parcella_calculated": return Number(p.parcella_calculated || 0);
        case "total_accrued": return Number(p.total_accrued || 0);
        case "total_invoiced": return Number(p.total_invoiced || 0);
        case "total_paid": return Number(p.total_paid || 0);
        case "cig": return p.cig || "";
        case "cup": return p.cup || "";
        default: return "";
      }
    };
    const rows = sorted.map((p) => Object.fromEntries(cols.map((c) => [c.label, rawFor(c, p)])));
    const ws = XLSX.utils.json_to_sheet(rows, { header: cols.map((c) => c.label) });
    // Column widths
    ws["!cols"] = cols.map((c) => ({ wch: Math.max(12, Math.round((c.w || 120) / 8)) }));
    // Number format for currency columns
    const currencyKeys = new Set(["Importo lavori", "Parcella (tecnico)", "Maturato", "Fatturato", "Pagato"]);
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = 1; R <= range.e.r; ++R) {
      cols.forEach((c, i) => {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: i });
        const cell = ws[cellRef];
        if (!cell) return;
        if (currencyKeys.has(c.label)) cell.z = '€ #,##0.00';
        if (c.key === "deadline" && cell.v instanceof Date) cell.z = 'dd/mm/yyyy';
      });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Gare & Progetti");
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `gare_filtrate_${today}.xlsx`);
    toast.success(`${sorted.length} righe esportate`);
  };

  const cellFor = (c, p) => {
    switch (c.key) {
      case "title":
        return (
          <div className="min-w-0">
            <Link to={`/projects/${p.id}`} className="font-medium text-neutral-900 hover:brand-text block truncate" title={p.title}>{truncate(p.title, 60)}</Link>
            {p.cig && <div className="mono-label mt-0.5">CIG {p.cig}</div>}
            {p.description && <div className="text-xs text-neutral-500 mt-1 truncate" title={p.description}>{truncate(p.description, 70)}</div>}
          </div>
        );
      case "client_name":
        return <span className="text-neutral-700 truncate block" title={p.client_name}>{p.client_name}</span>;
      case "type":
        return (
          <span className="inline-flex items-center gap-1 mono-label">
            {p.type === "pa" ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
            {p.type === "pa" ? "PA" : "Privato"}
          </span>
        );
      case "deadline": return <span className="font-mono text-xs text-neutral-700">{fmtDateShort(p.deadline)}</span>;
      case "_tl": return <span className={`inline-block px-2 py-0.5 border font-mono text-[10px] font-semibold ${p._tl_obj.className}`}>{p._tl_obj.level === "green" ? "CONS." : p._tl_obj.label}</span>;
      case "importo_lavori": return <span className="font-mono text-neutral-800 tabular-nums">{fmtEUR(p.importo_lavori)}</span>;
      case "parcella_calculated": return <span className="font-mono font-semibold text-neutral-900 tabular-nums">{fmtEUR(p.parcella_calculated)}</span>;
      case "total_accrued": return <span className="font-mono text-neutral-600 tabular-nums">{fmtEUR(p.total_accrued)}</span>;
      case "total_invoiced": return <span className="font-mono text-blue-700 tabular-nums">{fmtEUR(p.total_invoiced)}</span>;
      case "total_paid": return <span className="font-mono text-emerald-700 tabular-nums">{fmtEUR(p.total_paid)}</span>;
      case "cig": return <span className="font-mono text-xs">{p.cig || "—"}</span>;
      case "cup": return <span className="font-mono text-xs">{p.cup || "—"}</span>;
      default: return null;
    }
  };

  const totalFor = (k) => {
    if (k === "importo_lavori") return fmtEUR(totals.importo);
    if (k === "parcella_calculated") return fmtEUR(totals.parcella);
    if (k === "total_accrued") return fmtEUR(totals.accrued);
    if (k === "total_invoiced") return fmtEUR(totals.invoiced);
    if (k === "total_paid") return fmtEUR(totals.paid);
    return null;
  };

  return (
    <div className="p-8 lg:p-10 space-y-6 fade-up">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="mono-label">Archivio</div>
          <h1 className="font-display text-4xl font-medium mt-1">Elenco Gare & Progetti</h1>
          <p className="text-sm text-neutral-500 mt-2">Tabella completa con scadenze, valori economici e stato fatturazione. {sorted.length} risultati.</p>
        </div>
        <Button onClick={() => nav("/projects/new")} data-testid="new-project-btn" className="brand-bg hover:opacity-90"><Plus className="h-4 w-4 mr-1.5" /> Nuova pratica</Button>
      </header>

      {/* Filters */}
      <div className="border border-neutral-200 bg-white p-4 flex items-center gap-3 flex-wrap">
        <input type="text" placeholder="Cerca titolo, cliente, CIG..." value={query} onChange={(e) => setQuery(e.target.value)} data-testid="list-search" className="flex-1 min-w-[240px] border border-neutral-300 px-3 py-1.5 text-sm bg-white" />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="list-type" className="border border-neutral-300 px-2 py-1.5 text-sm bg-white">
          <option value="">Tutti i tipi</option>
          <option value="pa">PA</option>
          <option value="private">Privato</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="list-status" className="border border-neutral-300 px-2 py-1.5 text-sm bg-white">
          <option value="">Tutti gli stati</option>
          <option value="red">Rosso</option>
          <option value="orange">Arancione</option>
          <option value="yellow">Giallo</option>
          <option value="green">Consegnata</option>
          <option value="none">Senza scadenza</option>
        </select>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="cols-btn"><Columns3 className="h-4 w-4 mr-1.5" /> Colonne <span className="mono-label ml-1">{visibleCols.length}</span></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Colonne visibili</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {ALL_COLUMNS.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.key}
                checked={visibleCols.includes(c.key)}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => !c.alwaysOn && toggleCol(c.key)}
                disabled={c.alwaysOn}
                data-testid={`col-toggle-${c.key}`}
              >
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={resetCols} data-testid="cols-reset">Ripristina default</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="outline" size="sm" onClick={exportXlsx} disabled={sorted.length === 0} data-testid="export-filtered-btn">
          <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Esporta Excel
        </Button>
      </div>

      {/* Table */}
      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" style={{ tableLayout: "fixed" }}>
          <colgroup>
            {cols.map((c) => <col key={c.key} style={{ width: c.w }} />)}
            <col style={{ width: 40 }} />
          </colgroup>
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              {cols.map((c) => (
                <th key={c.key}
                  onClick={() => c.sortable && toggleSort(c.key)}
                  className={`px-3 py-3 mono-label cursor-pointer select-none ${c.align === "right" ? "text-right" : "text-left"}`}
                  data-testid={`sort-${c.key}`}>
                  <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "justify-end w-full" : ""}`}>
                    {c.label} <SortIcon k={c.key} />
                  </span>
                </th>
              ))}
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.id} className="border-b border-neutral-100 hover:bg-neutral-50 group" data-testid={`row-${p.id}`}>
                {cols.map((c) => (
                  <td key={c.key} className={`px-3 py-3 align-top ${c.align === "right" ? "text-right" : ""}`}>
                    {cellFor(c, p)}
                  </td>
                ))}
                <td className="px-3 py-3 align-top">
                  <Link to={`/projects/${p.id}`} className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:brand-text transition inline-block">
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={cols.length + 1} className="p-10 text-center text-sm text-neutral-500">Nessun risultato.</td></tr>
            )}
          </tbody>
          {sorted.length > 0 && (
            <tfoot className="border-t-2 border-neutral-300 bg-neutral-50">
              <tr>
                {cols.map((c, i) => {
                  const t = totalFor(c.key);
                  if (i === 0) return <td key={c.key} className="px-3 py-3 mono-label">Totale ({sorted.length})</td>;
                  if (t == null) return <td key={c.key} />;
                  const cls = c.key === "parcella_calculated" ? "brand-text" : c.key === "total_invoiced" ? "text-blue-700" : c.key === "total_paid" ? "text-emerald-700" : "";
                  return <td key={c.key} className={`px-3 py-3 text-right font-mono font-semibold ${cls}`}>{t}</td>;
                })}
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
